import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Loading } from '@monorepo/shared/components/Loading';
import { Button } from '@monorepo/shared/componentsV2/Button';
import { Description } from '@monorepo/shared/componentsV2/Description';
import { FieldTypePreviewFactory } from '@monorepo/shared/componentsV2/fieldDataType/FieldTypePreview/FieldTypePreviewFactory';
import { AnalyticsSyncDetailsSection } from '@monorepo/shared/componentsV2/fullPageWizard/AnalyticsSyncWizardStep/AnalyticsSyncDetailsSection';
import {
  Content,
  Page,
  SectionContainer,
  SectionHeader,
} from '@monorepo/shared/componentsV2/fullPageWizard/styled/fullPageWizard';
import { Header } from '@monorepo/shared/componentsV2/Header';
import { List } from '@monorepo/shared/componentsV2/List';
import { StartDate } from '@monorepo/shared/componentsV2/styled/header';
import { VisuallyHidden } from '@monorepo/shared/componentsV2/VisuallyHidden';
import { useLinkOnSameOrg } from '@monorepo/shared/hooks/navigation/useLinkOnSame/useLinkOnSameOrg';
import { useHasOrgUpdatePermissionOrSuperAdmin } from '@monorepo/shared/hooks/permissions/useHasPermissions';
import { format } from 'date-fns';
import React, { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  BackButton,
  ButtonContainer,
} from '../../../../logDataSetup/src/components/LogDetail/styled';
import { useResourceTypeAssetsOrShowError } from '../../hooks/useResourceTypeAssetsOrShowError';
import { NamePropertyPreview } from '../BuiltInPropertyPreviews/NamePropertyPreview';
import { SitePropertyPreview } from '../BuiltInPropertyPreviews/SitePropertyPreview';

const StyledContent = styled(Content)`
  max-width: 615px;
`;

export function ResourceTypeDetailsPage() {
  const { organizationId, resourceTypeId } = useParams();
  const canEditOrgSettings =
    useHasOrgUpdatePermissionOrSuperAdmin(organizationId);
  const { isError, isLoading, resourceProperties, resourceType } =
    useResourceTypeAssetsOrShowError(organizationId, resourceTypeId);

  const navigate = useNavigate();
  const baseLink = useLinkOnSameOrg('/data-setup/resource-types');
  const handleBack = useCallback(
    () => navigate(baseLink),
    [navigate, baseLink],
  );
  const handleEdit = useCallback(
    () => navigate(`${baseLink}/${resourceTypeId}/edit`),
    [navigate, baseLink, resourceTypeId],
  );

  const backAction = (
    <BackButton onClick={handleBack}>
      <VisuallyHidden>Back</VisuallyHidden>
      <ArrowBackIcon />
    </BackButton>
  );

  if (!organizationId || !resourceTypeId) {
    throw new Error(
      'ResourceTypeDetailsPage was rendered in a context without organization and/or resource type id',
    );
  }

  if (isLoading) {
    return (
      <Page>
        <Header leftActions={backAction} title="Settings" />
        <Loading />
      </Page>
    );
  }

  if (isError) {
    return (
      <Page>
        <Header leftActions={backAction} title="Settings" />
      </Page>
    );
  }

  // Should never happen, a contract with the BE is that it either returns a value or 404
  if (!resourceType || !resourceProperties) {
    return null;
  }

  return (
    <Page>
      <Header
        centerActions={
          <StartDate>
            {`Created ${format(new Date(resourceType.createdAt), 'P')}`}
          </StartDate>
        }
        leftActions={backAction}
        rightActions={
          canEditOrgSettings ? (
            <ButtonContainer>
              <Button color="primary" onClick={handleEdit} variant="contained">
                Edit Resource type
              </Button>
            </ButtonContainer>
          ) : null
        }
        title={`${resourceType.name} Settings`}
      />
      <StyledContent>
        <SectionContainer>
          <SectionHeader>Description</SectionHeader>
          <Description>
            {resourceType.description || 'No description.'}
          </Description>
        </SectionContainer>
        <AnalyticsSyncDetailsSection
          datasetOriginId={resourceTypeId}
          organizationId={organizationId}
          datasetOriginLabel="Resource type"
        />
        <SectionContainer>
          <SectionHeader>Fields</SectionHeader>
          <List>
            <NamePropertyPreview key="name" />
            {resourceType.isSiteSpecific && <SitePropertyPreview key="site" />}
            {resourceProperties.map((property) => (
              <FieldTypePreviewFactory key={property.id} field={property} />
            ))}
          </List>
          {!resourceProperties.length && !resourceType.isSiteSpecific && (
            <Description>No fields</Description>
          )}
        </SectionContainer>
      </StyledContent>
    </Page>
  );
}
